import React from "react";
import styled, { keyframes } from "styled-components";
import { Device } from "../../../components/device";
import { HorizontalAlignment, VerticalAlignment, Size } from "../../../components/types";
import ImageCrop from "../UI/Image/ImageCrop";
import Text from "../../../components/Text/Text";
import { isBrowserContext } from "../../../util/ContextHelper";

/*
<Banner
     size="lg"
     imageSrc="https://via.placeholder.com/320x320"
     videoSrc="https://www.heyday.dk/media/1357/omos_graded_ny.mp4"
     videoSrcPortrait="https://www.heyday.dk/media/1888/heydayshowreel_tv_banner.mp4"
     horizontalAlign="left"
     verticalAlign="center"
     overlay={false}>
     <Text horizontalAlign="center">
        --Content inside--
    </Text>
</Banner>
*/

const absFullscreen = `
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
`;

const FadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;

const BannerWrap = styled.section`
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
    z-index: 0;

    &.banner--mobile-ratio-4-5 {
        //forces a 4:5 ratio
        ${Device.mobileMax} {
            padding-top: 125%;
        }
    }

    h1, .h1 {
        font-size: var(--text-4xl, 2.25rem);
        line-height: var(--leading-none, 1);
        margin-bottom: 1.5rem;
        font-weight: bold;
        letter-spacing: 0.025em;
        ${Device.tablet} {
            font-size: var(--text-5xl, 3.333rem);
            margin-bottom: 2rem;
        }
        ${Device.laptop} {
            letter-spacing: 0.05em;
            font-size: var(--text-6xl, 4.667rem);
        }
    }

    .banner__link-wrap {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        display: block;

        &:hover + .container a:after{
            max-width: 100%;
        }
        &:hover + div + .container a:after{
            max-width: 100%;
        }
    }



    .container {
        pointer-events: none;
    }
    
    .btn {
        pointer-events: all;
    }

`;

const BannerBg = styled.figure<BannerProps>`
    box-sizing: border-box;
    overflow: hidden;
    pointer-events: none;
    padding: 0;
    margin: 0;
    ${absFullscreen};

    img {
        box-sizing: border-box;
        display: block;
        pointer-events: none;
        ${absFullscreen};
        object-fit: cover;
    }
`;

const BannerVideoBg = styled.video<BannerProps>`
    box-sizing: border-box;
    overflow: hidden;
    margin: 0;
    pointer-events: none;
    ${absFullscreen};
    animation: ${FadeIn} .25s forwards;
    object-fit: cover;
`;

const BannerOverlay = styled.div`
    box-sizing: border-box;
    background-color: rgba(0,0,0, 0.5);
    ${absFullscreen};
    animation: ${FadeIn} .25s forwards;
    pointer-events: none;
`;

const BannerContent = styled.div<BannerProps>`
    box-sizing: border-box;
    position: relative;
    padding: 1rem;
    display: flex;
    margin: 0 auto;
    text-shadow: 0 0 3rem rgba(0,0,0,.3);
    min-height: ${props =>
        (props.size === "sm") ? "50vh" :
            (props.size === "md") ? "75vh" :
                (props.size === "lg") ? "100vh" :
                    "75vh"};

    align-items: ${props =>
        (props.verticalAlign === "top") ? "flex-start" :
            (props.verticalAlign === "bottom") ? "flex-end" :
                "center"};

    justify-content: ${props =>
        (props.horizontalAlign === "left") ? "flex-start" :
            (props.horizontalAlign === "right") ? "flex-end" :
                "center"};

    animation: ${FadeIn} .25s forwards;

    ${Device.laptop} {
        padding-top: 3rem;
        padding-bottom: 3rem;
    }
    ${Device.desktopLG} {
        min-height: ${props =>
        (props.size === "sm") ? "400px" :
            (props.size === "md") ? "66vh" :
                (props.size === "lg") ? "100vh" :
                    "66vh"};
    }

    //Mobile Landscape
    @media only screen
        and (max-device-width: 1000px)
        and (orientation: landscape) {
        min-height: 300px;
    }

    .banner--mobile-ratio-4-5 & {
        ${Device.mobileMax} {
            min-height: 0;
            ${absFullscreen};
        }
    }

    .text-wrap {
        position: relative;
        word-break: break-word;
        margin-bottom: 0;
        margin-left: 0;
        margin-right: 0;
        h2{
            text-transform: unset;
        }
    }
`;

interface BannerProps
{
    size?: Size;
    imageAlt?: string[] | string;
    imageSrc?: string[] | string;
    videoSrc?: string;
    videoSrcPortrait?: string;
    verticalAlign?: VerticalAlignment;
    horizontalAlign?: HorizontalAlignment;
    overlay?: boolean;
    linkWrap?: string;
}

const Banner: React.FC<BannerProps> = (props) =>
{
    let srcVideo = props.videoSrc;
    const srcVideoVertical = props.videoSrcPortrait;

    if (srcVideoVertical !== null)
    {
        const mqMobileVertical = isBrowserContext() && window.matchMedia("(max-width: 1023px) and (orientation: portrait)").matches || false;
        if (mqMobileVertical)
        {
            srcVideo = srcVideoVertical;
        }
    }

    const renderImages = () =>
    {
        const { imageSrc } = props;
        const imageAlts = props.imageAlt instanceof Array ? props.imageAlt : [props.imageAlt];
        if (imageSrc instanceof Array)
        {
            return imageSrc.map((src, i) =>
            {
                return <ImageCrop key={`bg_${i}`} alt={imageAlts[i]} src={src} width="100%" height="100%" resizeMode={"crop"} />;
            });
        }
        else if (imageSrc)
        {
            return <ImageCrop alt={imageAlts[0]} src={imageSrc} width="100%" height="100%" resizeMode={"crop"} />;
        }
        return null;
    };

    return (
        <BannerWrap className={props.size === "lg" || props.size === "xl" ? "banner--mobile-ratio-4-5" : ""}>
            {props.imageSrc ?
                <BannerBg {...props}>
                    {renderImages()}
                </BannerBg>
                : null}

            {props.videoSrc ?
                <BannerVideoBg autoPlay muted loop playsInline>
                    <source src={srcVideo?.replace("http://", "https://")} type="video/mp4" />
                    Your browser does not support the video tag.
                </BannerVideoBg>
                : null}

            {props.linkWrap ?
                <a href={props.linkWrap} className="banner__link-wrap"></a>
                : null}

            {props.overlay ? <BannerOverlay /> : null}

            {props.children ?
                <BannerContent className="container" {...props}>
                    <Text>
                        {props.children}
                    </Text>
                </BannerContent> : null}
        </BannerWrap>
    );
};

export default Banner;